import { z } from 'zod';

/**
 * Chat moderation violation categories
 */
export const ModerationCategorySchema = z.enum([
  'profanity',
  'abuse',
  'spam',
  'negativity',
  'off_platform',
  'impersonation',
  'fraud',
]);

export type ModerationCategory = z.infer<typeof ModerationCategorySchema>;

/**
 * Individual violation detected by moderation
 */
export const ModerationViolationSchema = z.object({
  category: ModerationCategorySchema,
  confidence: z.number().min(0).max(1),
  reason: z.string(),
});

export type ModerationViolation = z.infer<typeof ModerationViolationSchema>;

/**
 * Moderation result from AI analysis
 */
export const ModerationResultSchema = z.object({
  allowed: z.boolean(),
  violations: z.array(ModerationViolationSchema),
  /**
   * True when the model could not be reached and this message was never checked.
   *
   * Distinct from `allowed: true`, which means checked and clean. Both attempts
   * failing used to be reported as clean, which is how the same impersonation
   * scam was blocked on one send and delivered on the next — the caller had no
   * way to tell "nothing wrong with it" from "nobody looked".
   */
  undetermined: z.boolean().optional(),
});

export type ModerationResult = z.infer<typeof ModerationResultSchema>;

/**
 * Raw response from Bedrock moderation prompt
 */
export const BedrockModerationResponseSchema = z.object({
  violations: z.array(z.object({
    category: z.string(),
    confidence: z.number(),
    reason: z.string(),
  })),
});

export type BedrockModerationResponse = z.infer<typeof BedrockModerationResponseSchema>;

/**
 * Moderation options for configuration
 */
export const ModerationOptionsSchema = z.object({
  categories: z.array(ModerationCategorySchema).optional(),
  threshold: z.number().min(0).max(1).optional(),
  skipForAdmins: z.boolean().optional(),
});

export type ModerationOptions = z.infer<typeof ModerationOptionsSchema>;

/**
 * User-friendly error messages by category
 */
export const MODERATION_ERROR_MESSAGES: Record<ModerationCategory, string> = {
  profanity: "Your message contains language that isn't allowed. Please rephrase without profanity or offensive terms.",
  abuse: "Your message appears to contain hostile or abusive content. Please communicate respectfully with other users.",
  spam: "Your message looks like it might be spam or an advertisement. Please keep conversations focused on the listing.",
  negativity: "Your message seems overly negative or aggressive. Please rephrase in a more constructive way.",
  off_platform: "For your safety, please keep all transactions on the platform. Sharing contact info, payment links, or arranging off-platform deals is not allowed.",
  impersonation: "Your message appears to impersonate platform staff or support. Our team will never ask for your password or payment details via chat.",
  fraud: "For your safety, please pay and get paid through Sellwild checkout. Checks, wire transfers, outside escrow or shipping agents, up-front payments and requests for ID or bank details are not allowed in chat.",
};

/**
 * Get user-friendly error message for a moderation category
 */
export function getModerationErrorMessage(category: ModerationCategory): string {
  return MODERATION_ERROR_MESSAGES[category];
}
